import React, { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { combineClassNames } from '@common/utils/combineClassNames';
import { Document, DocumentFolder, File } from '@modules/documents/types';

import { DocumentIcon } from '@modules/documents/components/document-icon';
import Icon from '@common/components/icon';

import './document-list-item.scss';

type OwnProps = {
  item: Document;
  canNavigate: boolean;
  selectedFile: File | null;
  selectedFolder: DocumentFolder | null;
  setSelectedFile: (selectedFile: File | null) => void;
  setSelectedFolder: (selectedFile: DocumentFolder | null) => void;
  setNavigationPath: (fn: (current: DocumentFolder[]) => DocumentFolder[]) => void;
};

const DocumentListItem = ({
  item, selectedFile, selectedFolder, canNavigate,
  setNavigationPath, setSelectedFile, setSelectedFolder,
}: OwnProps) => {
  if (item.type === 'personal_folder') return null;

  const { t } = useTranslation();

  const selected = item.id === selectedFile?.id || item.id === selectedFolder?.id;
  const fullClassName = combineClassNames('DocumentListItem', {
    'DocumentListItem--selected': selected,
  });

  const handleNavigateToChildFolder = useCallback((e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    if (!item.is_folder) return;

    setSelectedFile(null);
    setSelectedFolder(null);

    setNavigationPath((current) => [...current, item]);
  }, [item, setSelectedFolder, setNavigationPath, setSelectedFile]);

  const handleSelect = useCallback(() => {
    if (item.is_folder) {
      setSelectedFile(null);
      setSelectedFolder(item.id === selectedFolder?.id ? null : item);
    } else {
      setSelectedFolder(null);
      setSelectedFile(item.id === selectedFile?.id ? null : item);
    }
  }, [item, selectedFile, selectedFolder, setSelectedFile, setSelectedFolder]);

  return (
    <div
      role="option"
      className={fullClassName}
      onClick={handleSelect}
    >
      <div className="DocumentListItem__Icon">
        <DocumentIcon item={item} />
      </div>
      <span className={`${!item.name ? 'tw-text-gray-400' : ''}`}>
        {item.name || t('knowledge_base:title_empty')}
      </span>
      {selected && <Icon className="DocumentListItem__SelectedIcon" type="check" />}
      {item.is_folder && canNavigate && (
        <Icon
          type="chevron_right"
          className="DocumentListItem__NavigateIcon"
          onClick={handleNavigateToChildFolder}
        />
      )}
    </div>
  );
};

export default DocumentListItem;
